/** Message from Flutter to Web */
export interface FlutterMessage<T = unknown> {
  type: string;
  data: T;
}

/** Subscription status values */
export type SubscriptionStatus =
  | "trial_active"
  | "subscription_active"
  | "non_subscriber"
  | "mandate_pause_in_app"
  | "mandate_revoked_psp"
  | "subscription_over"
  | "trial_over";

/** Support type values */
export type SupportType = "whatsapp" | "call";

/** Initial data received from Flutter */
export interface FlutterInitialData {
  token: string;
  platform: "ios" | "android";
  appVersion: string;
  buildNumber: string;
  dialect: string;
  language: string;
  isSubscribed: SubscriptionStatus;
  userName: string;
  phoneNumber: string;
  subscriptionExpiry: string;
  profileUrl: string;
  supportType: SupportType;
  subscriptionCta: string;
  subscriptionImage: string;
}

/** Message types sent TO Flutter */
export enum FlutterOutgoingMessageType {
  READY = "ready",
  REQUEST_DATA = "requestData",
  UPDATE = "update",
  NAVIGATION = "navigation",
  /** Open a generic URL in the device browser */
  OPEN_URL = "openUrl",
  /** Request Flutter to pause any playing videos */
  VIDEO_PAUSE = "videoPause",
  /**
   * Open an in-app deeplink via Flutter's DeeplinkManager (e.g. recommendation cards).
   * Distinct from OPEN_URL: Flutter intercepts this and navigates natively
   * instead of opening a browser. Handled by StageInAppWebViewScreen._handleWebViewMessage.
   * Expected data shape: { deeplink: string } — a share.stage.in URL.
   */
  DEEP_LINK = "deepLink",
}

/** Data for UPDATE message - sent when dialect or language changes */
export interface UpdateMessageData {
  dialect?: string;
  language?: string;
}

/** Message types received FROM Flutter */
export enum FlutterIncomingMessageType {
  DATA_UPDATE = "dataUpdate",
  SETTINGS_UPDATED = "settingsUpdated",
  /** Sent when app goes to background - pause videos */
  VIDEO_PAUSE = "videoPause",
  /** Sent when app comes to foreground - resume videos */
  VIDEO_RESUME = "videoResume",
  /** Sent when Flutter wants to restart video from beginning */
  VIDEO_RESTART = "videoRestart",
}

/** Settings page route names - used by both web and app */
export enum SettingsRouteNames {
  BACK_PRESS = "back-press",
  PROFILE = "profile",
  LANGUAGE_SETTINGS = "language-settings",
  DIALECT_SELECTION = "dialect-selection",
  REGISTER_TV = "register-tv",
  TV_SETUP_GUIDE = "tv-setup-guide",
  SUBSCRIPTION = "subscription",
  ABOUT_US = "about-us",
  CAREER = "career",
  PRIVACY = "privacy",
  TERMS = "terms",
  REFUND_POLICY = "refund-policy",
  CONTACT_US = "contact-us",
  LOGOUT = "logout",
  // Support route names
  SUPPORT_WHATSAPP = "whatsapp",
  SUPPORT_PHONE = "phone",
  // Subscription status route names
  SUBSCRIPTION_TRIAL_ACTIVE = "trial_active",
  SUBSCRIPTION_ACTIVE = "subscription_active",
  SUBSCRIPTION_NON_SUBSCRIBER = "non_subscriber",
  SUBSCRIPTION_MANDATE_PAUSE = "mandate_pause_in_app",
  SUBSCRIPTION_MANDATE_REVOKED = "mandate_revoked_psp",
  SUBSCRIPTION_OVER = "subscription_over",
  SUBSCRIPTION_TRIAL_OVER = "trial_over",
  CHECKOUT = "checkout",
  HOME = "home",
}

/** Navigation message data structure */
export interface NavigationMessageData {
  routeName: SettingsRouteNames;
}
